import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { roleHome } from '../utils/roleHome';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Inbox, Search, Filter, MailOpen, Archive, CheckCheck, ArrowLeft,
  Calendar, CreditCard, ShieldCheck, User, Settings, Megaphone,
  AlertTriangle, Loader2, X, ArchiveRestore, Trash2, CheckSquare, Square,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * MessagesCenter — Iter-268
 * Persistent, actionable inbox. Separate from bell notifications.
 *
 * Props (optional, for embedding):
 *   asPage     — when true (default) wraps with header + back nav
 *   pageTitle  — override the H1 (default "Messages")
 */
const CATEGORY_META = {
  events:         { label: 'Events',          icon: Calendar,    tone: 'text-cyan-700 bg-cyan-50 border-cyan-200' },
  payments:       { label: 'Payments',        icon: CreditCard,  tone: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  certifications: { label: 'Certifications',  icon: ShieldCheck, tone: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
  account:        { label: 'Account',         icon: User,        tone: 'text-amber-700 bg-amber-50 border-amber-200' },
  system:         { label: 'System',          icon: Settings,    tone: 'text-slate-700 bg-slate-50 border-slate-200' },
  admin_updates:  { label: 'Admin Updates',   icon: Megaphone,   tone: 'text-rose-700 bg-rose-50 border-rose-200' },
};

const PRIORITY_BORDER = {
  urgent:    'border-l-4 border-l-rose-500',
  important: 'border-l-4 border-l-amber-500',
  normal:    'border-l-4 border-l-slate-200',
};

// Iter-277 — swipe-left-to-delete wrapper.
//
// Mobile gesture: swipe a message card to the left to reveal a Delete
// action. Tapping Delete opens the existing confirmation dialog (no
// instant delete — per user spec). Desktop or non-touch devices fall
// back transparently — only `touchstart/move/end` events activate
// the swipe, so mouse/keyboard interactions remain untouched.
//
// Behavior:
//   * Drag distance < OPEN_THRESHOLD → snap back to 0
//   * Drag distance ≥ OPEN_THRESHOLD → snap open at OPEN_AT
//   * Only one row can be open at a time (parent owns `openId`)
//   * While open, tapping the card body snaps it shut without firing
//     the underlying onClick (so we never accidentally open the detail
//     sheet from a "close swipe" interaction)
//   * Disabled while in multi-select mode (avoids gesture conflicts)
const SWIPE = {
  OPEN_THRESHOLD: 60,   // px the user must drag past for it to "open"
  OPEN_AT:        96,   // px the row settles at when open
  MAX_DRAG:      140,   // hard cap so rubber-band feels physical
  ACTIVATE_AXIS:  10,   // px of horizontal movement before we hijack
};

const SwipeableRow = ({ children, onDeleteRequest, isOpen, onOpen, onClose, disabled }) => {
  const startX = React.useRef(null);
  const startY = React.useRef(null);
  const axisLocked = React.useRef(null); // 'h' | 'v' | null
  const [dx, setDx] = React.useState(0);

  // Sync displayed offset with parent-owned `isOpen` state so e.g.
  // tapping a different row collapses this one.
  React.useEffect(() => {
    setDx(isOpen ? -SWIPE.OPEN_AT : 0);
  }, [isOpen]);

  if (disabled) {
    return children;
  }

  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    axisLocked.current = null;
  };

  const handleTouchMove = (e) => {
    if (startX.current == null) return;
    const dxRaw = e.touches[0].clientX - startX.current;
    const dyRaw = e.touches[0].clientY - startY.current;
    // Lock to whichever axis the user moves on first — this stops a
    // vertical scroll from being misread as a left-swipe.
    if (axisLocked.current == null) {
      if (Math.abs(dxRaw) < SWIPE.ACTIVATE_AXIS && Math.abs(dyRaw) < SWIPE.ACTIVATE_AXIS) return;
      axisLocked.current = Math.abs(dxRaw) > Math.abs(dyRaw) ? 'h' : 'v';
    }
    if (axisLocked.current !== 'h') return;
    // Only left-swipe (negative dx) reveals delete. Right-swipe past
    // the open position closes the row.
    const offset = isOpen ? -SWIPE.OPEN_AT + dxRaw : dxRaw;
    const clamped = Math.max(-SWIPE.MAX_DRAG, Math.min(0, offset));
    setDx(clamped);
  };

  const handleTouchEnd = () => {
    if (axisLocked.current === 'h') {
      if (dx <= -SWIPE.OPEN_THRESHOLD) onOpen();
      else onClose();
    }
    startX.current = null;
    startY.current = null;
    axisLocked.current = null;
  };

  return (
    <div className="relative overflow-hidden rounded-lg" data-testid="swipeable-row">
      {/* Delete action revealed under the card */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDeleteRequest(); }}
        className="absolute inset-y-0 right-0 w-24 bg-rose-600 hover:bg-rose-700 text-white flex flex-col items-center justify-center gap-0.5 transition"
        style={{ opacity: dx < -8 ? 1 : 0, pointerEvents: dx < -SWIPE.OPEN_THRESHOLD ? 'auto' : 'none' }}
        aria-label="Delete message"
        data-testid="swipe-delete-action"
        tabIndex={isOpen ? 0 : -1}
      >
        <Trash2 className="w-5 h-5" />
        <span className="text-[11px] font-semibold">Delete</span>
      </button>
      {/* Card itself — slides over the delete action */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClickCapture={(e) => {
          // While open, the first tap closes the row instead of
          // bubbling to the card's onClick (which would open the
          // detail sheet).
          if (isOpen) {
            e.stopPropagation();
            e.preventDefault();
            onClose();
          }
        }}
        style={{
          transform: `translateX(${dx}px)`,
          transition: startX.current == null ? 'transform 180ms ease-out' : 'none',
          touchAction: 'pan-y',
        }}
      >
        {children}
      </div>
    </div>
  );
};

const fmtTime = (s) => {
  if (!s) return '';
  const date = new Date(s);
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
};

const MessagesCenter = ({ asPage = true, pageTitle = 'Messages' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Iter-417 — never navigate a user into a route outside their role scope.
  // A talent tapping an /admin/* deep_link would otherwise be bounced
  // role-mismatch → '/' → '/login' (a phantom logout). Deep links are now
  // role-safe server-side; this is client-side defence in depth.
  const safeNavigate = (link) => {
    if (!link || typeof link !== 'string' || !link.startsWith('/')) return;
    const role = user?.role;
    const isAdmin = role === 'admin' || role === 'super_admin';
    const seg = link.split('?')[0];
    let out = link;
    if (!isAdmin) {
      if (seg.startsWith('/admin/')) out = roleHome(role);
      else if (role === 'talent' && seg.startsWith('/client/')) out = roleHome(role);
      else if ((role === 'client' || role === 'agency') && seg.startsWith('/talent/')) out = roleHome(role);
    }
    navigate(out);
  };

  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total_unread: 0, total_urgent: 0, by_category: {} });
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  // Iter-274 — multi-select inbox controls.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // 'selected' | 'all' | message obj | null
  // Iter-274 — modal fallback when a card has no deep_link.
  const [detailMessage, setDetailMessage] = useState(null);
  // Iter-277 — id of the row currently swiped-open. Only one row at a
  // time can show its Delete action.
  const [openSwipeId, setOpenSwipeId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (search) params.append('search', search);
      if (showArchived) params.append('archived', 'true');
      params.append('limit', '100');

      const [list, sum] = await Promise.all([
        api.get(`/messages?${params.toString()}`),
        api.get('/messages/summary'),
      ]);
      setMessages(list.data.results || []);
      setTotal(list.data.total || 0);
      setSummary(sum.data || { total_unread: 0, total_urgent: 0, by_category: {} });
    } catch (e) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [category, showArchived]);

  // Debounce search a bit so we don't spam the API
  useEffect(() => {
    const t = setTimeout(fetchAll, search ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const handleMessageClick = async (m) => {
    // Iter-274 — in select mode, taps toggle selection instead of opening.
    if (selectMode) {
      setSelectedIds((curr) => {
        const next = new Set(curr);
        if (next.has(m.id)) next.delete(m.id); else next.add(m.id);
        return next;
      });
      return;
    }
    const isEventChannel = m.metadata?.is_event_channel || (m.id || '').startsWith('channel:');

    // Iter-276 — Event channel (chat) cards keep their direct-to-thread
    // behavior because they ARE conversations, not notifications. Every
    // other card type opens the detail sheet on the same page so the
    // user can actually READ the message body. Per user spec: "tap →
    // detail view should open with full title/body/timestamp/category,
    // and an 'Open related page' button only if there's a destination."
    if (isEventChannel && m.deep_link) {
      // Mark read (event channel cards do this server-side via
      // EventChannelDetail when the user lands).
      safeNavigate(m.deep_link);
      return;
    }

    // PO-2026-06 — a direct-message reply card is itself a conversation
    // pointer: one tap opens the exact DM (never the general center).
    const dlPath = (m.deep_link || '').split('?')[0];
    if (m.deep_link && dlPath === '/talent/support') {
      safeNavigate(m.deep_link);
      return;
    }

    // Open the detail sheet FIRST. Mark-as-read happens atomically with
    // the sheet opening so the read state is in sync with what the user
    // actually saw (constraint: "Mark as read only after the message
    // detail opens successfully").
    setDetailMessage(m);
    if (!m.read_at) {
      const isNotification = m.metadata?.is_notification || (m.id || '').startsWith('notif:');
      // Optimistic local update — flip read_at and decrement counters
      // before the network call so the badge moves immediately.
      setMessages((curr) => curr.map((x) => (x.id === m.id ? { ...x, read_at: new Date().toISOString() } : x)));
      setSummary((s) => {
        const cat = m.category || 'system';
        const prev = s.by_category?.[cat] || { unread: 0, unread_urgent: 0 };
        const urgent = m.priority === 'urgent';
        return {
          ...s,
          total_unread: Math.max(0, s.total_unread - 1),
          total_urgent: Math.max(0, s.total_urgent - (urgent ? 1 : 0)),
          by_category: {
            ...(s.by_category || {}),
            [cat]: {
              unread: Math.max(0, prev.unread - 1),
              unread_urgent: Math.max(0, prev.unread_urgent - (urgent ? 1 : 0)),
            },
          },
        };
      });
      // Server write — both persisted user_messages and synthetic
      // `notif:<id>` ids are accepted by /messages/{id}/read (the
      // backend has handled the synthetic prefix since Iter-271).
      try { await api.post(`/messages/${m.id}/read`); } catch (_) { /* noop */ }
    }
  };

  // Iter-274 — selection helpers.
  const toggleSelectMode = () => {
    setSelectMode((on) => !on);
    setSelectedIds(new Set());
  };
  const selectAllVisible = () => {
    setSelectedIds(new Set(messages.map((m) => m.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const deleteOne = async (m) => {
    try {
      await api.delete(`/messages/${m.id}`);
      toast.success('Message deleted');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      const r = await api.post('/messages/bulk-delete', {
        message_ids: Array.from(selectedIds),
      });
      toast.success(`Removed ${selectedIds.size} message${selectedIds.size === 1 ? '' : 's'}`, {
        description: r?.data?.dismissed
          ? `${r.data.deleted || 0} deleted · ${r.data.dismissed} dismissed`
          : undefined,
      });
      clearSelection();
      setSelectMode(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const deleteAll = async () => {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      const r = await api.post(`/messages/delete-all${params.toString() ? `?${params}` : ''}`);
      toast.success(`Deleted ${r?.data?.deleted ?? 0} messages`);
      clearSelection();
      setSelectMode(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete-all failed');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/messages/mark-all-read', category ? { category } : {});
      toast.success('Marked all as read');
      fetchAll();
    } catch (e) { toast.error('Failed to mark all read'); }
  };

  const handleArchive = async (m, e) => {
    e.stopPropagation();
    try {
      await api.post(`/messages/${m.id}/${m.archived_at ? 'unarchive' : 'archive'}`);
      fetchAll();
    } catch (e) { toast.error('Archive failed'); }
  };

  const filteredCategoryButtons = useMemo(() => (
    ['', ...Object.keys(CATEGORY_META)].map((c) => {
      const meta = CATEGORY_META[c];
      const unread = summary.by_category?.[c]?.unread || 0;
      return { value: c, label: c ? meta.label : 'All', unread, urgent: summary.by_category?.[c]?.unread_urgent || 0 };
    })
  ), [summary]);

  const content = (
    <div className="max-w-3xl mx-auto px-4 py-4 space-y-3" data-testid="messages-center">
      {/* CATEGORY CHIPS */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {filteredCategoryButtons.map((c) => (
              <Button
                key={c.value || 'all'}
                size="sm"
                variant={category === c.value ? 'default' : 'outline'}
                onClick={() => setCategory(c.value)}
                className={category === c.value ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : ''}
                data-testid={`messages-category-${c.value || 'all'}-btn`}
              >
                {c.label}
                {c.unread > 0 && (
                  <Badge className="ml-1.5 bg-rose-500 text-white border-0 text-[10px] px-1.5 py-0">{c.unread}</Badge>
                )}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search messages..."
                className="w-full pl-8 pr-3 py-2 border rounded text-sm"
                data-testid="messages-search-input"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowArchived(!showArchived)}
              data-testid="messages-toggle-archived-btn"
            >
              {showArchived ? <ArchiveRestore className="w-4 h-4 mr-1" /> : <Archive className="w-4 h-4 mr-1" />}
              {showArchived ? 'Active' : 'Archived'}
            </Button>
            {!showArchived && summary.total_unread > 0 && (
              <Button
                size="sm"
                onClick={handleMarkAllRead}
                className="bg-slate-700 hover:bg-slate-800 text-white"
                data-testid="messages-mark-all-read-btn"
              >
                <CheckCheck className="w-4 h-4 mr-1" /> Mark all read
              </Button>
            )}
          </div>

          {/* Iter-274 — multi-select / delete toolbar. Always visible so
              users can find delete controls without hunting. */}
          <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-slate-100">
            {!selectMode ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={toggleSelectMode}
                  data-testid="messages-select-mode-btn"
                  disabled={messages.length === 0}
                >
                  <CheckSquare className="w-4 h-4 mr-1" /> Select
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmDelete('all')}
                  className="text-rose-700 border-rose-200 hover:bg-rose-50"
                  data-testid="messages-delete-all-btn"
                  disabled={messages.length === 0}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete all{category ? ' in category' : ''}
                </Button>
                {/* Iter-275 — auto-archive informational note. */}
                <span
                  className="text-[11px] text-slate-400 italic ml-auto leading-tight"
                  data-testid="auto-archive-note"
                  title="Unread, important, or urgent messages are never auto-archived."
                >
                  Read messages auto-archive after 30 days.
                </span>
              </>
            ) : (
              <>
                <span className="text-xs text-slate-600 font-semibold" data-testid="select-count">
                  {selectedIds.size} selected
                </span>
                <Button size="sm" variant="outline" onClick={selectAllVisible} data-testid="messages-select-all-btn">
                  Select all visible
                </Button>
                <Button size="sm" variant="outline" onClick={clearSelection} data-testid="messages-clear-selection-btn" disabled={selectedIds.size === 0}>
                  Clear
                </Button>
                <Button
                  size="sm"
                  onClick={() => setConfirmDelete('selected')}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  disabled={selectedIds.size === 0}
                  data-testid="messages-delete-selected-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete selected
                </Button>
                <Button size="sm" variant="ghost" onClick={toggleSelectMode} data-testid="messages-cancel-select-btn">
                  Cancel
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* LIST */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
        </div>
      ) : messages.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-slate-500" data-testid="messages-empty-state">
            <Inbox className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <div className="font-medium">{showArchived ? 'No archived messages' : "You're all caught up"}</div>
            <div className="text-xs mt-1">
              {showArchived ? 'Archived messages will appear here.' : 'Important payment, event and certification updates will show up here.'}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {messages.map((m) => {
            const cat = CATEGORY_META[m.category] || CATEGORY_META.system;
            const Icon = cat.icon;
            const unread = !m.read_at;
            const isEventChannel = m.metadata?.is_event_channel || (m.id || '').startsWith('channel:');
            const isSynthetic = isEventChannel || m.metadata?.is_notification || (m.id || '').startsWith('notif:');
            const unreadCount = m.metadata?.unread_count;
            return (
              <SwipeableRow
                key={m.id}
                isOpen={openSwipeId === m.id}
                onOpen={() => setOpenSwipeId(m.id)}
                onClose={() => setOpenSwipeId((c) => (c === m.id ? null : c))}
                onDeleteRequest={() => { setOpenSwipeId(null); setConfirmDelete(m); }}
                disabled={selectMode || isEventChannel /* don't let users swipe to delete a live chat thread */}
              >
              <Card
                className={`${PRIORITY_BORDER[m.priority] || PRIORITY_BORDER.normal} hover:shadow-md cursor-pointer transition-shadow ${unread ? 'bg-white' : 'bg-slate-50/60'} ${selectMode && selectedIds.has(m.id) ? 'ring-2 ring-cyan-400 bg-cyan-50/40' : ''}`}
                onClick={() => handleMessageClick(m)}
                data-testid={`message-row-${m.id}`}
              >
                <CardContent className="p-3 flex items-start gap-3">
                  {selectMode && (
                    <div className="shrink-0 pt-1.5" data-testid={`message-select-${m.id}`}>
                      {selectedIds.has(m.id) ? (
                        <CheckSquare className="w-5 h-5 text-cyan-600" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-300" />
                      )}
                    </div>
                  )}
                  <div className={`shrink-0 p-2 rounded-full ${cat.tone}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-sm ${unread ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                        {m.title}
                      </span>
                      {m.priority === 'urgent' && (
                        <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px] px-1.5 py-0">
                          <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> URGENT
                        </Badge>
                      )}
                      {m.priority === 'important' && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] px-1.5 py-0">
                          IMPORTANT
                        </Badge>
                      )}
                      {unread && !m.archived_at && (
                        <span className="w-2 h-2 rounded-full bg-cyan-500 shrink-0" data-testid="message-unread-dot" />
                      )}
                      {isEventChannel && unreadCount > 1 && (
                        <Badge className="bg-cyan-100 text-cyan-800 border-cyan-200 text-[10px] px-1.5 py-0">
                          {unreadCount} new
                        </Badge>
                      )}
                    </div>
                    {m.body && <p className="text-xs text-slate-600 line-clamp-2">{m.body}</p>}
                    <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-2">
                      <span>{cat.label}</span>
                      <span>·</span>
                      <span>{fmtTime(m.created_at)}</span>
                      {m.archived_at && <><span>·</span><span className="italic">archived</span></>}
                    </div>
                  </div>
                  {!isSynthetic && !selectMode && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleArchive(m, e)}
                        className="text-slate-400 hover:text-slate-700"
                        data-testid={`message-${m.id}-archive-btn`}
                        aria-label={m.archived_at ? 'Unarchive' : 'Archive'}
                      >
                        {m.archived_at ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(m); }}
                        className="text-slate-400 hover:text-rose-600"
                        data-testid={`message-${m.id}-delete-btn`}
                        aria-label="Delete message"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              </SwipeableRow>
            );
          })}
        </div>
      )}

      {total > messages.length && (
        <div className="text-center text-xs text-slate-400 py-2">
          Showing {messages.length} of {total} messages
        </div>
      )}

      {/* Iter-274 — Delete confirmation modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="confirm-delete-overlay"
        >
          <div
            className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            data-testid="confirm-delete-dialog"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="shrink-0 p-2 rounded-full bg-rose-50">
                <Trash2 className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {confirmDelete === 'all' && `Delete all ${category ? CATEGORY_META[category]?.label.toLowerCase() : 'inbox'} messages?`}
                  {confirmDelete === 'selected' && `Delete ${selectedIds.size} message${selectedIds.size === 1 ? '' : 's'}?`}
                  {typeof confirmDelete === 'object' && 'Delete this message?'}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {confirmDelete === 'all'
                    ? 'This cannot be undone. Synthesized notification cards will be dismissed.'
                    : 'This cannot be undone.'}
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setConfirmDelete(null)} data-testid="confirm-cancel-btn">
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="confirm-delete-btn"
                onClick={async () => {
                  const target = confirmDelete;
                  setConfirmDelete(null);
                  if (target === 'all') await deleteAll();
                  else if (target === 'selected') await deleteSelected();
                  else if (target && typeof target === 'object') await deleteOne(target);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Iter-274 — Detail modal for cards that have no deep_link
          (defensive: every tap produces a visible response). */}
      {detailMessage && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4"
          onClick={() => setDetailMessage(null)}
          data-testid="message-detail-overlay"
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            data-testid="message-detail-dialog"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className={`shrink-0 p-2 rounded-full ${(CATEGORY_META[detailMessage.category] || CATEGORY_META.system).tone}`}>
                {(() => { const I = (CATEGORY_META[detailMessage.category] || CATEGORY_META.system).icon; return <I className="w-5 h-5" />; })()}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-slate-900">{detailMessage.title}</h3>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {(CATEGORY_META[detailMessage.category] || CATEGORY_META.system).label}
                  {' · '}{fmtTime(detailMessage.created_at)}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDetailMessage(null)} data-testid="detail-close-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>
            {detailMessage.body && (
              <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{detailMessage.body}</p>
            )}
            {!detailMessage.body && (
              <p className="text-sm italic text-slate-400">No additional details.</p>
            )}
            {/* Iter-276 — priority badge inside the sheet so user can see
                this is urgent/important without scanning back to the list. */}
            {detailMessage.priority && detailMessage.priority !== 'normal' && (
              <div className="mt-3">
                <span className={`text-[11px] uppercase font-semibold px-2 py-0.5 rounded ${
                  detailMessage.priority === 'urgent'
                    ? 'bg-rose-100 text-rose-700'
                    : 'bg-amber-100 text-amber-700'
                }`} data-testid="detail-priority-badge">
                  {detailMessage.priority}
                </span>
              </div>
            )}
            <div className="flex gap-2 justify-end mt-4">
              {/* Iter-276 — "Open related page" button only renders when
                  the message actually has a destination. Per user spec:
                  hide entirely when there is no related page. */}
              {detailMessage.deep_link && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const link = detailMessage.deep_link;
                    setDetailMessage(null);
                    safeNavigate(link);
                  }}
                  data-testid="detail-open-related-btn"
                >
                  Open related page
                </Button>
              )}
              <Button size="sm" onClick={() => setDetailMessage(null)} data-testid="detail-ok-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white">
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (!asPage) return content;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Inbox className="w-5 h-5 text-cyan-600" />
              {pageTitle}
              {summary.total_unread > 0 && (
                <Badge className="bg-rose-500 text-white border-0">{summary.total_unread}</Badge>
              )}
            </h1>
            <p className="text-xs text-slate-500">
              Action items, event updates, payment notices, and certification status.
            </p>
          </div>
        </div>
      </header>
      {content}
    </div>
  );
};

export default MessagesCenter;
